import { 
  ref, 
  query, 
  orderByChild, 
  limitToLast, 
  get,
  onValue
} from 'firebase/database';
import { database } from './core';
import {
  setData,
  updateData,
  createWithKey,
  updateMultipleSafe
} from './api-wrapper.service';
import {
  Notification,
  TagNotification,
  NewFollowerNotification,
  MentionNotification,
  CompetitionNotification,
  SystemNotification
} from '../../types/notification';
import { adaptLegacyNotification } from '../../utils/notification-adapter';
import { subscriptionManager } from './subscription-manager';
import logger from '../../utils/logger';

// Maximum notifications to fetch at once
const DEFAULT_LIMIT = 20;

/**
 * Get user notifications with pagination - fetches from all three notification paths
 * 
 * @param userId The user ID to get notifications for
 * @param limit Maximum number of notifications to return
 * @param startAfterTimestamp Return notifications before this timestamp
 * @returns Promise resolving to an array of notifications
 */
export const getUserNotifications = async (
  userId: string, 
  limit: number = DEFAULT_LIMIT, 
  startAfterTimestamp?: number
): Promise<Notification[]> => {
  try {
    // Fetch from all three notification paths
    const allNotifications: Notification[] = [];
    
    // Helper function to fetch from a specific path
    const fetchFromPath = async (path: string, markAsType?: { isSysGen?: boolean; isAdditional?: boolean }) => {
      const notifRef = ref(database, `${path}/${userId}`);
      const notifQuery = query(
        notifRef,
        orderByChild('timestamp'), // Use legacy field name
        limitToLast(Math.ceil(limit / 3)) // Divide limit among the three paths
      );
      
      const snapshot = await get(notifQuery);
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const rawNotif = childSnapshot.val();
          rawNotif.key = childSnapshot.key;
          
          // Mark notification type
          if (markAsType?.isSysGen) {
            rawNotif.isSysGen = true;
          }
          if (markAsType?.isAdditional) {
            rawNotif.isAdditional = true;
          }
          
          // Adapt legacy notification format
          const notification = adaptLegacyNotification(rawNotif);
          allNotifications.push(notification);
        });
      }
    };
    
    // Fetch from all three paths in parallel
    await Promise.all([
      fetchFromPath('notifications'),
      fetchFromPath('systemNotifications', { isSysGen: true }),
      fetchFromPath('additionalNotifications', { isAdditional: true })
    ]);
    
    // Sort all notifications by date descending (newest first)
    return allNotifications.sort((a, b) => {
      const aTime = (a as any).timestamp || a.dateCreated || 0;
      const bTime = (b as any).timestamp || b.dateCreated || 0;
      return bTime - aTime;
    }).slice(0, limit); // Ensure we don't exceed the requested limit
  } catch (error) {
    // If permissions error, just return empty array instead of throwing
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('Permission denied')) {
      logger.warn('Permission denied accessing notifications, returning empty array');
      return [];
    }
    logger.error('Error fetching notifications:', error);
    // Return empty array instead of throwing to prevent UI errors
    return [];
  }
};

/**
 * Get unread notification count for a user
 *
 * NOTE: Matches original app behavior - counts unread notifications client-side
 * by iterating through all notification paths.
 *
 * @param userId The user ID to count notifications for
 * @returns Promise resolving to the unread count
 */
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    // Count unread notifications from all three paths (matches original app)
    let totalUnreadCount = 0;

    // Helper function to count unread notifications in a specific path
    const countUnreadInPath = async (path: string) => {
      const notifRef = ref(database, `${path}/${userId}`);
      const notifSnapshot = await get(notifRef);

      if (notifSnapshot.exists()) {
        notifSnapshot.forEach((childSnapshot) => {
          const notification = childSnapshot.val();
          // Read-state is tracked via `isRead` — the only field the RTDB rules
          // allow writing on notifications, so `read` (which can never be
          // flipped in the DB) must not factor into the unread count.
          const isUnread = notification.isRead !== true;
          if (isUnread) {
            totalUnreadCount++;
          }
        });
      }
    };

    // Count unread notifications from all three paths
    await Promise.all([
      countUnreadInPath('notifications'),
      countUnreadInPath('systemNotifications'),
      countUnreadInPath('additionalNotifications')
    ]);

    return totalUnreadCount;
  } catch (error) {
    // If permissions error, just return 0 instead of throwing
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('Permission denied')) {
      logger.warn('Permission denied accessing notifications, returning 0');
      return 0;
    }
    logger.error('Error fetching unread notification count:', error);
    // Return 0 instead of throwing to prevent UI errors
    return 0;
  }
};

/**
 * Subscribe to unread notification count changes
 *
 * NOTE: Matches original app behavior - subscribes to all three notification paths
 * and counts unread notifications client-side.
 *
 * @param userId The user ID to subscribe for
 * @param callback Function to call when count changes
 */
export const subscribeToUnreadCount = (
  userId: string,
  callback: (count: number) => void
): (() => void) => {
  const unsubscribers: (() => void)[] = [];
  let regularCount = 0;
  let systemCount = 0;
  let additionalCount = 0;

  // Helper to count unread in a snapshot
  const countUnreadInSnapshot = (snapshot: any): number => {
    let count = 0;
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot: any) => {
        const notification = childSnapshot.val();
        // Read-state is tracked via `isRead` (see getUnreadNotificationCount).
        const isUnread = notification.isRead !== true;
        if (isUnread) {
          count++;
        }
      });
    }
    return count;
  };

  // Subscribe to regular notifications
  const notificationsRef = ref(database, `notifications/${userId}`);
  const notifListener = onValue(notificationsRef, (snapshot) => {
    regularCount = countUnreadInSnapshot(snapshot);
    callback(regularCount + systemCount + additionalCount);
  }, (error: Error) => {
    if ((error as any).code === 'PERMISSION_DENIED') {
      logger.info('Permission denied accessing notifications - user may not have data yet');
    } else {
      logger.error('Error in notifications subscription:', error);
    }
    regularCount = 0;
    callback(regularCount + systemCount + additionalCount);
  });
  // onValue returns the unsubscribe function — passing it to off() as the
  // callback matches nothing and leaks the listener
  unsubscribers.push(notifListener);

  // Subscribe to system notifications
  const systemNotificationsRef = ref(database, `systemNotifications/${userId}`);
  const systemListener = onValue(systemNotificationsRef, (snapshot) => {
    systemCount = countUnreadInSnapshot(snapshot);
    callback(regularCount + systemCount + additionalCount);
  }, (error: Error) => {
    if ((error as any).code === 'PERMISSION_DENIED') {
      logger.info('Permission denied accessing system notifications - user may not have data yet');
    } else {
      logger.error('Error in system notifications subscription:', error);
    }
    systemCount = 0;
    callback(regularCount + systemCount + additionalCount);
  });
  unsubscribers.push(systemListener);

  // Subscribe to additional notifications (tag notifications)
  const additionalNotificationsRef = ref(database, `additionalNotifications/${userId}`);
  const additionalListener = onValue(additionalNotificationsRef, (snapshot) => {
    additionalCount = countUnreadInSnapshot(snapshot);
    callback(regularCount + systemCount + additionalCount);
  }, (error: Error) => {
    if ((error as any).code === 'PERMISSION_DENIED') {
      logger.info('Permission denied accessing additional notifications - user may not have data yet');
    } else {
      logger.error('Error in additional notifications subscription:', error);
    }
    additionalCount = 0;
    callback(regularCount + systemCount + additionalCount);
  });
  unsubscribers.push(additionalListener);

  // Register with subscription manager for global cleanup
  const subscriptionKey = `notification-unread-count:${userId}`;
  const combinedUnsubscribe = () => {
    unsubscribers.forEach(unsub => unsub());
  };
  subscriptionManager.add(subscriptionKey, combinedUnsubscribe);

  // Return unsubscribe function that also removes from manager
  return () => {
    subscriptionManager.remove(subscriptionKey);
  };
};

/**
 * Mark a notification as read
 *
 * NOTE: Matches original app - just updates isRead field, no separate count path.
 *
 * @param userId The user ID who owns the notification
 * @param notificationId The notification ID to mark as read
 * @param notificationType The type of notification to determine the correct path
 */
export const markNotificationAsRead = async (
  userId: string,
  notificationId: string,
  notificationType?: 'regular' | 'system' | 'additional'
): Promise<void> => {
  try {
    // Determine the correct path based on notification type
    let notificationPath = `notifications/${userId}/${notificationId}`;

    if (notificationType === 'system') {
      notificationPath = `systemNotifications/${userId}/${notificationId}`;
    } else if (notificationType === 'additional') {
      notificationPath = `additionalNotifications/${userId}/${notificationId}`;
    }

    const notificationRef = ref(database, notificationPath);

    // Check if notification exists and get current state
    const snapshot = await get(notificationRef);
    if (!snapshot.exists()) {
      logger.warn(`Notification not found at path: ${notificationPath}`);
      return;
    }

    const notification = snapshot.val();

    // Read-state MUST be written via `isRead` only. The RTDB rules allow the
    // owner to write `isRead` on all three notification trees but deny writes to
    // a `read` child (it isn't in the schema) — writing `read` fails with
    // PERMISSION_DENIED and aborts the whole operation. Modern notifications are
    // created with `read: false` and no `isRead`; the old code wrote whichever
    // field existed (`read`), so those could never be marked read. Always write
    // `isRead`.
    if (notification.isRead !== true) {
      await updateData(notificationPath, { isRead: true });
      // NOTE: No decrementUnreadCount - original app counts client-side
    }
  } catch (error) {
    logger.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 *
 * NOTE: Matches original app - updates isRead field on each notification.
 * No separate notificationCounts path (original app doesn't use one).
 *
 * @param userId The user ID to mark all notifications as read
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const updates: Record<string, any> = {};

    // Helper function to process notifications from a specific path
    const processNotifications = async (path: string) => {
      const notifRef = ref(database, `${path}/${userId}`);
      const snapshot = await get(notifRef);

      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const notification = childSnapshot.val();
          const notificationId = childSnapshot.key;

          // Read-state is tracked via `isRead` — the only rule-writable field.
          const isUnread = notification.isRead !== true;

          if (isUnread && notificationId) {
            updates[`${path}/${userId}/${notificationId}/isRead`] = true;
          }

        });
      }
    };

    // Process all three types of notifications
    await Promise.all([
      processNotifications('notifications'),
      processNotifications('systemNotifications'),
      processNotifications('additionalNotifications')
    ]);

    // If there are updates to make
    if (Object.keys(updates).length > 0) {
      // Use safer version to avoid root permission errors
      await updateMultipleSafe(updates);
      // NOTE: No notificationCounts path update - original app counts client-side
    }
  } catch (error) {
    logger.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Create a tag notification
 */
export const createTagNotification = async (
  recipientId: string,
  senderId: string,
  senderName: string,
  senderPhotoURL: string | undefined,
  contentId: string,
  contentType: 'training' | 'competition' | 'beltPromotion' | 'post' | 'exercise',
  contentPreview?: string
): Promise<string> => {
  const notification: TagNotification = {
    id: '', // Will be set during creation
    type: 'tag',
    recipientId,
    senderId,
    senderName,
    senderPhotoURL,
    contentId,
    contentType,
    contentPreview,
    read: false,
    dateCreated: Date.now(),
    title: `${senderName} tagged you in a ${contentType}`,
    body: contentPreview ? `"${contentPreview}"` : undefined
  };
  
  return createNotification(recipientId, notification);
};

/**
 * Create a new follower notification
 */
export const createNewFollowerNotification = async (
  recipientId: string,
  senderId: string,
  senderName: string,
  senderPhotoURL: string | undefined
): Promise<string> => {
  const notification: NewFollowerNotification = {
    id: '', // Will be set during creation
    type: 'newFollower',
    recipientId,
    senderId,
    senderName,
    senderPhotoURL,
    read: false,
    dateCreated: Date.now(),
    title: `${senderName} started following you`,
    body: `${senderName} is now following your activities`
  };
  
  return createNotification(recipientId, notification);
};

/**
 * Create a mention notification
 */
export const createMentionNotification = async (
  recipientId: string,
  senderId: string,
  senderName: string,
  senderPhotoURL: string | undefined,
  contentId: string,
  contentType: 'comment' | 'post' | 'training',
  contentPreview?: string
): Promise<string> => {
  const notification: MentionNotification = {
    id: '', // Will be set during creation
    type: 'mention',
    recipientId,
    senderId,
    senderName,
    senderPhotoURL,
    contentId,
    contentType,
    contentPreview,
    read: false,
    dateCreated: Date.now(),
    title: `${senderName} mentioned you`,
    body: contentPreview ? `"${contentPreview}"` : `${senderName} mentioned you in a ${contentType}`
  };
  
  return createNotification(recipientId, notification);
};

/**
 * Create a competition notification
 */
export const createCompetitionNotification = async (
  recipientId: string,
  senderId: string,
  senderName: string,
  senderPhotoURL: string | undefined,
  competitionId: string,
  competitionName: string,
  medals?: {
    gold?: number;
    silver?: number;
    bronze?: number;
  }
): Promise<string> => {
  const notification: CompetitionNotification = {
    id: '', // Will be set during creation
    type: 'competition',
    recipientId,
    senderId,
    senderName,
    senderPhotoURL,
    competitionId,
    competitionName,
    medals,
    read: false,
    dateCreated: Date.now(),
    title: `${senderName} competed in ${competitionName}`,
    body: getMedalSummary(senderName, medals)
  };
  
  return createNotification(recipientId, notification);
};

/**
 * Create a system notification
 */
export const createSystemNotification = async (
  recipientId: string,
  title: string,
  body?: string,
  actionUrl?: string,
  actionLabel?: string,
  priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium'
): Promise<string> => {
  const notification: SystemNotification = {
    id: '', // Will be set during creation
    type: 'system',
    recipientId,
    read: false,
    dateCreated: Date.now(),
    title,
    body,
    actionUrl,
    actionLabel,
    priority
  };
  
  return createNotification(recipientId, notification);
};

/**
 * Delete a notification
 *
 * @param userId The user ID who owns the notification
 * @param notificationId The notification ID to delete
 * @param notificationType The type of notification to determine the correct path
 */
export const deleteNotification = async (
  userId: string,
  notificationId: string,
  notificationType?: 'regular' | 'system' | 'additional'
): Promise<void> => {
  try {
    // Try to find the notification in all paths if type not specified
    const paths = notificationType
      ? [notificationType === 'system' ? 'systemNotifications' :
         notificationType === 'additional' ? 'additionalNotifications' :
         'notifications']
      : ['notifications', 'systemNotifications', 'additionalNotifications'];

    for (const path of paths) {
      const notificationRef = ref(database, `${path}/${userId}/${notificationId}`);
      const snapshot = await get(notificationRef);

      if (snapshot.exists()) {
        // Delete the notification
        await setData(`${path}/${userId}/${notificationId}`, null);
        // NOTE: No decrementUnreadCount - original app counts client-side from isRead field
        break; // Found and deleted, no need to check other paths
      }
    }
  } catch (error) {
    logger.error('Error deleting notification:', error);
    throw error;
  }
};

// Helper functions

/**
 * Base function to create a notification
 *
 * NOTE: Original app doesn't maintain a separate unread count - it's calculated client-side
 */
const createNotification = async (
  recipientId: string,
  notification: Notification
): Promise<string> => {
  try {
    // Don't send notification to yourself
    if (notification.type !== 'system' && 'senderId' in notification && notification.senderId === recipientId) {
      return '';
    }

    // Create notification with auto-generated key
    const notificationId = await createWithKey(`notifications/${recipientId}`, notification);
    notification.id = notificationId;

    // NOTE: No incrementUnreadCount - original app counts client-side from isRead field

    return notificationId;
  } catch (error) {
    logger.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Format medal summary for competition notifications
 */
const getMedalSummary = (
  name: string,
  medals?: {
    gold?: number;
    silver?: number;
    bronze?: number;
  }
): string => {
  if (!medals) {
    return `${name} competed in a competition`;
  }
  
  const { gold = 0, silver = 0, bronze = 0 } = medals;
  const totalMedals = gold + silver + bronze;
  
  if (totalMedals === 0) {
    return `${name} competed in a competition`;
  }
  
  const medalParts = [];
  if (gold > 0) {
    medalParts.push(`${gold} gold${gold > 1 ? 's' : ''}`);
  }
  if (silver > 0) {
    medalParts.push(`${silver} silver${silver > 1 ? 's' : ''}`);
  }
  if (bronze > 0) {
    medalParts.push(`${bronze} bronze${bronze > 1 ? 's' : ''}`);
  }
  
  const medalText = medalParts.join(', ');
  return `${name} won ${medalText}`;
};


/**
 * Subscribe to all notification changes (regular, system, and additional)
 * 
 * @param userId The user ID to subscribe to
 * @param callbacks Object with callbacks for each notification type
 * @returns Unsubscribe function
 */
export const subscribeToAllNotifications = (
  userId: string,
  callbacks: {
    onNotifications?: (notifications: Notification[]) => void;
    onSystemNotifications?: (notifications: Notification[]) => void;
    onAdditionalNotifications?: (notifications: Notification[]) => void;
  }
): (() => void) => {
  const unsubscribers: (() => void)[] = [];

  // Subscribe to regular notifications
  if (callbacks.onNotifications) {
    const notificationsRef = ref(database, `notifications/${userId}`);
    const notificationsQuery = query(
      notificationsRef,
      orderByChild('timestamp'), // Legacy field name
      limitToLast(100)
    );
    
    const notificationListener = onValue(notificationsQuery, (snapshot) => {
      if (!snapshot.exists()) {
        callbacks.onNotifications!([]);
        return;
      }
      
      const notifications: Notification[] = [];
      snapshot.forEach((childSnapshot) => {
        const rawNotif = childSnapshot.val();
        rawNotif.key = childSnapshot.key;
        
        // Adapt legacy notification format
        const notification = adaptLegacyNotification(rawNotif);
        notifications.push(notification);
      });
      
      // Sort by date descending (newest first) - use timestamp field
      callbacks.onNotifications!(notifications.sort((a, b) => {
        const aTime = (a as any).timestamp || a.dateCreated || 0;
        const bTime = (b as any).timestamp || b.dateCreated || 0;
        return bTime - aTime;
      }));
    }, (error: Error) => {
      if ((error as any).code === 'PERMISSION_DENIED') {
        logger.info('Permission denied accessing notifications - user may not have data yet');
      } else {
        logger.error('Error in notifications subscription:', error);
      }
      callbacks.onNotifications!([]);
    });

    // onValue returns the unsubscribe function — push it directly
    unsubscribers.push(notificationListener);
  }

  // Subscribe to system notifications
  if (callbacks.onSystemNotifications) {
    const systemNotificationsRef = ref(database, `systemNotifications/${userId}`);
    const systemNotificationsQuery = query(
      systemNotificationsRef,
      orderByChild('timestamp'), // Legacy field name
      limitToLast(100)
    );
    
    const systemListener = onValue(systemNotificationsQuery, (snapshot) => {
      if (!snapshot.exists()) {
        callbacks.onSystemNotifications!([]);
        return;
      }
      
      const notifications: Notification[] = [];
      snapshot.forEach((childSnapshot) => {
        const rawNotif = childSnapshot.val();
        rawNotif.key = childSnapshot.key;
        rawNotif.isSysGen = true; // Mark as system generated
        
        // Adapt legacy notification format
        const notification = adaptLegacyNotification(rawNotif);
        notifications.push(notification);
      });
      
      // Sort by date descending (newest first) - use timestamp field
      callbacks.onSystemNotifications!(notifications.sort((a, b) => {
        const aTime = (a as any).timestamp || a.dateCreated || 0;
        const bTime = (b as any).timestamp || b.dateCreated || 0;
        return bTime - aTime;
      }));
    }, (error: Error) => {
      if ((error as any).code === 'PERMISSION_DENIED') {
        logger.info('Permission denied accessing system notifications - user may not have data yet');
      } else {
        logger.error('Error in system notifications subscription:', error);
      }
      callbacks.onSystemNotifications!([]);
    });

    unsubscribers.push(systemListener);
  }

  // Subscribe to additional notifications
  if (callbacks.onAdditionalNotifications) {
    const additionalNotificationsRef = ref(database, `additionalNotifications/${userId}`);
    const additionalNotificationsQuery = query(
      additionalNotificationsRef,
      orderByChild('timestamp'), // Legacy field name
      limitToLast(100)
    );
    
    const additionalListener = onValue(additionalNotificationsQuery, (snapshot) => {
      if (!snapshot.exists()) {
        callbacks.onAdditionalNotifications!([]);
        return;
      }
      
      const notifications: Notification[] = [];
      snapshot.forEach((childSnapshot) => {
        const rawNotif = childSnapshot.val();
        rawNotif.key = childSnapshot.key;
        rawNotif.isAdditional = true; // Mark as additional
        
        // Adapt legacy notification format
        const notification = adaptLegacyNotification(rawNotif);
        notifications.push(notification);
      });
      
      // Sort by date descending (newest first) - use timestamp field
      callbacks.onAdditionalNotifications!(notifications.sort((a, b) => {
        const aTime = (a as any).timestamp || a.dateCreated || 0;
        const bTime = (b as any).timestamp || b.dateCreated || 0;
        return bTime - aTime;
      }));
    }, (error: Error) => {
      if ((error as any).code === 'PERMISSION_DENIED') {
        logger.info('Permission denied accessing additional notifications - user may not have data yet');
      } else {
        logger.error('Error in additional notifications subscription:', error);
      }
      callbacks.onAdditionalNotifications!([]);
    });

    unsubscribers.push(additionalListener);
  }

  // Return combined unsubscribe function
  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
};